import { useCallback } from 'react'

import { useApi } from '../api/useApi'
import { useAddChatToast } from '../store/toasts'
import { getQueueForChat, isTurnLive, useMessagesStore, useReadAllChatMessages } from '../store'
import { useSubmitMessage } from './useSubmitMessage'

type RedoResponse = {
  data: {
    userMessageId: string
    deletedMessageIds: Array<string>
  }
}

const STILL_STREAMING = 'The conversation is still streaming. Wait for it to finish.'

function redoFailureMessage(status: number): string {
  if (status === 403) return 'Redo is limited to Highlight developer accounts.'
  if (status === 409) return STILL_STREAMING
  return `The server refused the request (${status}).`
}

/**
 * Developer-only redo of an AI turn. The backend truncates the transcript back
 * to the user message that opened the turn, then a promptless submit re-drives
 * the agent over what is left. Promptless is what a tool response already uses,
 * so the turn commits no new user bubble.
 *
 * The truncation has to happen server-side. A skill run's kickoff prompt never
 * reaches the client, so replaying it from here would drop the skill.
 */
export function useRedoTurn() {
  const { post } = useApi()
  const readAllMessages = useReadAllChatMessages()
  const { setMessages } = useMessagesStore()
  const submitMessage = useSubmitMessage()
  const addChatToast = useAddChatToast()

  return useCallback(
    async (conversationId: string, messageId: string) => {
      // Read liveness at click time rather than subscribing: `useTurnActivity`
      // hands history rows a permanently-false `isTurnActive` (it gates them
      // off the streaming atoms on purpose), so their button stays enabled
      // mid-turn and would otherwise reach the endpoint just to be 409'd.
      if (isTurnLive(conversationId)) {
        addChatToast({ title: 'Could not redo this turn', message: STILL_STREAMING, type: 'error' })
        return
      }

      // A promptless submit lands in the queue behind pending sends rather
      // than re-running the turn, so those sends would drain against a
      // transcript this redo already truncated. Bail before truncating.
      if (getQueueForChat(conversationId).length > 0) {
        addChatToast({
          title: 'Could not redo this turn',
          message: 'Send or cancel the queued messages first.',
          type: 'error',
        })
        return
      }

      let deletedMessageIds: Array<string>
      try {
        const response = await post(`conversations/${conversationId}/redo`, JSON.stringify({ messageId }), {
          version: 'v1',
          headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
          addChatToast({
            title: 'Could not redo this turn',
            message: redoFailureMessage(response.status),
            type: 'error',
          })
          return
        }
        deletedMessageIds = ((await response.json()) as RedoResponse).data.deletedMessageIds
      } catch (error) {
        console.error('Failed to truncate turn for redo:', error)
        addChatToast({ title: 'Could not redo this turn', message: 'Please try again.', type: 'error' })
        return
      }

      // Drop what the backend deleted before re-driving, so the replacement
      // turn streams in below the user message rather than under the stale one.
      const deleted = new Set(deletedMessageIds)
      const remaining = (readAllMessages()[conversationId] ?? []).filter((message) => !deleted.has(message.id))
      setMessages(conversationId, remaining)

      // Never rejects. A failed turn surfaces through the usual turn-error UI.
      await submitMessage(null, { conversationId, preserveDraft: true })
    },
    [post, readAllMessages, setMessages, submitMessage, addChatToast],
  )
}
